/**
 * PaperPreviewList – shared scrollable paper-card list for bulk download UIs.
 *
 * Handles:
 * - Optional loading spinner
 * - Scrollable list of WorkCards (with optional "included" source work)
 * - Custom empty-state slot
 * - Caller-provided `footer` overlay (absolute-positioned at the bottom)
 *
 */

import React from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';

import { BundleWork } from './types';
import { WorkCard } from './WorkCard';
import _ListContainer from "PaperPreviewList-ListContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/PaperPreviewList.tsx?styleId=ListContainer";
import _ScrollArea from "PaperPreviewList-ScrollArea.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/PaperPreviewList.tsx?styleId=ScrollArea";
import _LoadingState from "PaperPreviewList-LoadingState.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/PaperPreviewList.tsx?styleId=LoadingState";
import _Spinner from "PaperPreviewList-Spinner.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/PaperPreviewList.tsx?styleId=Spinner";
import _BlueFadeFooter from "PaperPreviewList-BlueFadeFooter.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/PaperPreviewList.tsx?styleId=BlueFadeFooter";
import _FadeFooterInner from "PaperPreviewList-FadeFooterInner.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/PaperPreviewList.tsx?styleId=FadeFooterInner";
import _WhiteGradient from "PaperPreviewList-WhiteGradient.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/PaperPreviewList.tsx?styleId=WhiteGradient";


export interface PaperPreviewListProps {
  /** Related works to display (excluding the source/included work). */
  works: BundleWork[];
  /** Optional source/included work shown first with a header. */
  includedWork?: BundleWork;
  /** Header text for the included work card. */
  includedWorkHeader?: string;
  /** Show a loading spinner instead of the list. */
  loading?: boolean;
  /** Text shown below the spinner. */
  loadingMessage?: string;
  /** Custom empty-state content rendered when there are no works. */
  emptyState?: React.ReactNode;
  /**
   * Overlay rendered at the bottom of the list (absolute-positioned).
   * Use `<PaperPreviewList.FadeFooter>` for the blue-gradient "+N papers"
   * treatment, or pass any custom element (e.g. a white gradient overlay).
   */
  footer?: React.ReactNode;
  /** Show admin-only debug annotations (bucket, similarity score). */
  showAnnotations?: boolean;
  /** Show relevancy badges on cards (default true). */
  showRelevancy?: boolean;
  /** CSS max-height for the scrollable area. */
  maxHeight?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function PaperPreviewList({
  works,
  includedWork,
  includedWorkHeader = 'The paper you are reading (included)',
  loading = false,
  loadingMessage = 'Preparing your PDF pack\u2026',
  emptyState,
  footer,
  showAnnotations = false,
  showRelevancy = true,
  maxHeight,
  className,
  style,
}: PaperPreviewListProps) {
  const hasContent = works.length > 0 || !!includedWork;

  return (
    <ListContainer className={className} style={style}>
      {loading ? (
        <LoadingState>
          <Spinner aria-hidden />
          <Body size="md" as="div" style={primaryText}>
            {loadingMessage}
          </Body>
        </LoadingState>
      ) : !hasContent && emptyState ? (
        <>{emptyState}</>
      ) : (
        <>
          <ScrollArea style={maxHeight ? { maxHeight } : undefined}>
            {includedWork && (
              <WorkCard
                work={includedWork}
                header={includedWorkHeader}
                showRelevancy={false}
                showAnnotations={showAnnotations}
              />
            )}

            {works.map((work) => (
              <WorkCard
                key={work.id}
                work={work}
                showRelevancy={showRelevancy}
                showAnnotations={showAnnotations}
              />
            ))}
          </ScrollArea>

          {footer}
        </>
      )}
    </ListContainer>
  );
}

// ---------------------------------------------------------------------------
// Pre-built footer variants – importable by consumers
// ---------------------------------------------------------------------------

/** Blue-gradient fade footer with "+N related research papers" label. */
export function FadeFooter({
  hiddenPapersCount,
}: {
  hiddenPapersCount: number;
}) {
  if (hiddenPapersCount <= 0) return null;
  return (
    <BlueFadeFooter>
      <FadeFooterInner>
        <Body size="lg" bold as="strong">
          +{hiddenPapersCount}
        </Body>
        <Body size="xs" as="span">
          related research papers
        </Body>
      </FadeFooterInner>
    </BlueFadeFooter>
  );
}

/** Simple white-to-transparent gradient overlay (no text). */
export function GradientOverlay() {
  return <WhiteGradient />;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const primaryText: React.CSSProperties = {
  color: 'var(--text-primary, #141413)',
};

const ListContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ListContainer",
  styles: _ListContainer
});

const ScrollArea = /*#__PURE__*/styled("div", null, {
  displayName: "ScrollArea",
  styles: _ScrollArea
});

const LoadingState = /*#__PURE__*/styled("div", null, {
  displayName: "LoadingState",
  styles: _LoadingState
});

const Spinner = /*#__PURE__*/styled("div", null, {
  displayName: "Spinner",
  styles: _Spinner
});

const BlueFadeFooter = /*#__PURE__*/styled("div", null, {
  displayName: "BlueFadeFooter",
  styles: _BlueFadeFooter
});

const FadeFooterInner = /*#__PURE__*/styled("div", null, {
  displayName: "FadeFooterInner",
  styles: _FadeFooterInner
});

const WhiteGradient = /*#__PURE__*/styled("div", null, {
  displayName: "WhiteGradient",
  styles: _WhiteGradient
});

export default PaperPreviewList;
